/**
 * @fileoverview Performance by SKU Table component with expandable country rows.
 * @module shared/components/ui/PerformanceBySkuTable
 */

import React, { ReactElement, useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/utils/cn'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Skeleton,
  Badge,
  Button,
  TableHeaderMenu,
  DraggableTableHead,
  ReturnsIndicator,
  ColumnShelf,
} from '@/shared/components/ui'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { useCountryProductPerformance, useProductPerformance, useProductCancellations } from '@/shared/hooks/dashboard'
import { useDashboardData } from '@/shared/hooks/dashboard/useDashboardData'
import { useLabelResolver } from '@/shared/hooks/useLabelResolver'
import { useProductGroupsMetadata } from '@/shared/hooks/useProductGroupsMetadata'
import { useTableColumnState } from '@/shared/hooks'
import { useCompactMoney } from '@/shared/hooks/useCompactMoney'
import { formatPercent, formatInt } from '@/shared/utils/metricsUtils'
import { codeToFlag } from '@/shared/utils/countryUtils'
import { Thumbnail } from '@/shared/components/ui/Thumbnail'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable'
import { ArrowLeftRight, ChevronDown, EyeOff, FilterX, MoreVertical, PanelRight } from 'lucide-react'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { useIsMobile, useIsBelowDesktop } from '@/shared/hooks/useIsMobile'
import { useColumnShelfDnd, EMPTY_HEADER_DROP_ID, SHELF_TOGGLE_DROP_ID } from '@/shared/hooks/useColumnShelfDnd'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'

/**
 * Props for the PerformanceBySkuTable component.
 */
interface PerformanceBySkuTableProps {
  /** Additional CSS classes */
  className?: string
}

/**
 * Computed country totals with derived metrics.
 */
interface CountryTotals {
  /** Unique identifier (country code or name) */
  id: string
  /** Display name of the country */
  name: string
  /** Country code (e.g., 'US', 'CA') */
  code: string
  /** Total quantity sold */
  quantity: number
  /** Total sales revenue */
  sales: number
  /** Total advertising spend */
  adSpend: number
  /** Platform breakdown of advertising spend */
  adPlatformBreakdown: Record<string, number>
  /** Total cost of goods sold */
  cogs: number
  /** Total expenses (adSpend + cogs) */
  totalExpenses: number
  /** Net profit (sales - totalExpenses) */
  profit: number
  /** Return on ad spend ratio */
  roas: number
  /** Profit margin as ratio */
  margin: number
}

/**
 * Computed SKU metrics with derived KPIs.
 */
interface SkuMetrics {
  /** SKU identifier */
  sku: string
  /** Units sold */
  quantity: number
  /** Sales revenue */
  sales: number
  /** Advertising spend */
  adSpend: number
  /** Platform breakdown of advertising spend */
  adPlatformBreakdown: Record<string, number>
  /** Cost of goods sold */
  cogs: number
  /** Total expenses (adSpend + cogs) */
  totalExpenses: number
  /** Net profit (sales - totalExpenses) */
  profit: number
  /** Return on ad spend ratio */
  roas: number
  /** Profit margin as ratio */
  margin: number
}

/**
 * Helper to extract platform spend from breakdown.
 */
function getPlatformSpend(breakdown: Record<string, number>, platformSearch: string[]): number {
  let total = 0
  for (const [key, amount] of Object.entries(breakdown)) {
    const lowerKey = key.toLowerCase()
    if (platformSearch.some(s => lowerKey.includes(s))) {
      total += amount
    }
  }
  return total
}

/**
 * Aggregated totals row, shaped like CountryTotals/SkuMetrics so it can be
 * rendered through the same cell renderer.
 *
 * Additive metrics (sales, adSpend, cogs, etc.) are summed; ratio metrics
 * (roas, margin) are RECOMPUTED from the aggregates — summing per-row ratios
 * would be mathematically wrong.
 */
interface TotalsRow {
  quantity: number
  sales: number
  adSpend: number
  adPlatformBreakdown: Record<string, number>
  cogs: number
  totalExpenses: number
  profit: number
  roas: number
  margin: number
}

/**
 * Compute aggregated totals from a set of rows. Ratios are derived from the
 * summed numerator/denominator (blended), not averaged.
 */
function computeTotals(
  items: ReadonlyArray<{
    quantity: number
    sales: number
    adSpend: number
    adPlatformBreakdown: Record<string, number>
    cogs: number
    totalExpenses: number
    profit: number
  }>
): TotalsRow {
  const summed = items.reduce(
    (acc, item) => {
      acc.quantity += item.quantity
      acc.sales += item.sales
      acc.adSpend += item.adSpend
      acc.cogs += item.cogs
      acc.totalExpenses += item.totalExpenses
      acc.profit += item.profit
      for (const [platform, amount] of Object.entries(item.adPlatformBreakdown)) {
        acc.adPlatformBreakdown[platform] = (acc.adPlatformBreakdown[platform] ?? 0) + amount
      }
      return acc
    },
    {
      quantity: 0,
      sales: 0,
      adSpend: 0,
      adPlatformBreakdown: {} as Record<string, number>,
      cogs: 0,
      totalExpenses: 0,
      profit: 0,
    }
  )

  return {
    ...summed,
    roas: summed.adSpend > 0 ? summed.sales / summed.adSpend : 0,
    margin: summed.sales > 0 ? summed.profit / summed.sales : 0,
  }
}

/**
 * Loading state component with skeleton rows.
 */
function LoadingState(): ReactElement {
  const { t } = useTranslation('features-home')
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h3 className="text-xl font-semibold text-foreground">
          {t('tables.performanceBySku.title')}
        </h3>
        <Skeleton className="h-9 w-24" />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('tables.performanceBySku.columns.countrySku')}</TableHead>
            <TableHead className="text-right">
              {t('tables.performanceBySku.columns.quantity')}
            </TableHead>
            <TableHead className="text-right">
              {t('tables.performanceBySku.columns.sales')}
            </TableHead>
            <TableHead className="text-right">
              {t('tables.performanceBySku.columns.adSpend')
                .split('\n')
                .map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
            </TableHead>
            <TableHead className="text-right">
              {t('tables.performanceBySku.columns.costOfGoods')
                .split('\n')
                .map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
            </TableHead>
            <TableHead className="text-right">
              {t('tables.performanceBySku.columns.totalExpenses')
                .split('\n')
                .map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
            </TableHead>
            <TableHead className="text-right">
              {t('tables.performanceBySku.columns.profit')}
            </TableHead>
            <TableHead className="text-right">
              {t('tables.performanceBySku.columns.roas')}
            </TableHead>
            <TableHead className="text-right">
              {t('tables.performanceBySku.columns.profitMargin')
                .split('\n')
                .map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-5 w-32" />
              </TableCell>
              <TableCell className="text-right">
                <Skeleton className="h-5 w-16" />
              </TableCell>
              <TableCell className="text-right">
                <Skeleton className="h-5 w-20" />
              </TableCell>
              <TableCell className="text-right">
                <Skeleton className="h-5 w-20" />
              </TableCell>
              <TableCell className="text-right">
                <Skeleton className="h-5 w-20" />
              </TableCell>
              <TableCell className="text-right">
                <Skeleton className="h-5 w-20" />
              </TableCell>
              <TableCell className="text-right">
                <Skeleton className="h-5 w-20" />
              </TableCell>
              <TableCell className="text-right">
                <Skeleton className="h-5 w-16" />
              </TableCell>
              <TableCell className="text-right">
                <Skeleton className="h-5 w-16" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

/**
 * Error state component.
 */
function ErrorState({ message }: { message: string }): ReactElement {
  const { t } = useTranslation('features-home')
  return (
    <div>
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-foreground">
          {t('tables.performanceBySku.title')}
        </h3>
      </div>
      <p className="text-destructive">{message}</p>
    </div>
  )
}

/**
 * Empty state component.
 */
function EmptyState(): ReactElement {
  const { t } = useTranslation('features-home')
  return (
    <div>
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-foreground">
          {t('tables.performanceBySku.title')}
        </h3>
      </div>
      <p className="text-muted-foreground">{t('tables.performanceBySku.noData')}</p>
    </div>
  )
}

/**
 * Droppable placeholder header rendered when no metric column is visible —
 * the only chip drop target in that state. Dropping (or clicking) a chip
 * appends the column at the end of the order.
 */
function EmptyColumnsDropHead({ label }: { label: string }): ReactElement {
  const { setNodeRef, isOver } = useDroppable({ id: EMPTY_HEADER_DROP_ID })
  return (
    <TableHead ref={setNodeRef} className="px-4 py-3 text-right">
      <span
        className={cn(
          'inline-flex items-center rounded-lg border border-dashed border-border px-3 py-1.5 text-xs font-normal text-muted-foreground transition-colors',
          isOver && 'border-ring bg-ring/10 text-foreground'
        )}
      >
        {label}
      </span>
    </TableHead>
  )
}

/**
 * Desktop shelf toggle that doubles as a header drop zone: dropping a column
 * header on it hides the column — the always-available remove target while
 * the shelf is closed. During a header drag the icon flips to EyeOff and the
 * button highlights, stronger while the dragged header hovers it.
 */
function ShelfToggleDropButton({
  shelfOpen,
  isDragActive,
  onToggle,
  label,
}: {
  shelfOpen: boolean
  isDragActive: boolean
  onToggle: () => void
  label: string
}): ReactElement {
  const { setNodeRef, isOver } = useDroppable({ id: SHELF_TOGGLE_DROP_ID })
  return (
    <Button
      ref={setNodeRef}
      variant="ghost"
      onClick={onToggle}
      aria-pressed={shelfOpen}
      className={cn(
        'h-10 rounded-none border-l border-border px-4 text-muted-foreground hover:text-foreground',
        shelfOpen && 'bg-accent text-accent-foreground',
        isDragActive && 'bg-ring/10 text-foreground',
        isDragActive && isOver && 'bg-ring/25'
      )}
    >
      {isDragActive ? <EyeOff className="h-4 w-4" /> : <PanelRight className="h-4 w-4" />}
      {label}
    </Button>
  )
}

type ColumnId =
  | 'country'
  | 'sales'
  | 'adSpend'
  | 'googleAds'
  | 'metaAds'
  | 'cogs'
  | 'totalExpenses'
  | 'profit'
  | 'roas'
  | 'margin'

// Note: 'country' is always visible (not toggleable), so it's not included here
// Default to 3 columns (mobile minimum) so it works on all breakpoints
const DEFAULT_VISIBLE_COLUMNS: Set<ColumnId> = new Set([
  'sales',
  'adSpend',
  'profit',
])

const DEFAULT_COLUMN_ORDER: ColumnId[] = [
  'country',
  'sales',
  'adSpend',
  'googleAds',
  'metaAds',
  'cogs',
  'totalExpenses',
  'profit',
  'roas',
  'margin',
]

const COLUMN_STATE_STORAGE_KEY = 'performance_by_sku_table'
const SHELF_OPEN_STORAGE_KEY = 'performance_by_sku_table_shelf_open'

/** Metric categories for grouping shelf chips under captions. */
const COLUMN_GROUPS: { id: string; defaultLabel: string; columns: ColumnId[] }[] = [
  { id: 'revenue', defaultLabel: 'Revenue', columns: ['sales'] },
  { id: 'ads', defaultLabel: 'Ads', columns: ['adSpend', 'googleAds', 'metaAds'] },
  { id: 'costs', defaultLabel: 'Costs', columns: ['cogs', 'totalExpenses'] },
  { id: 'profitability', defaultLabel: 'Profitability', columns: ['profit', 'roas', 'margin'] },
]

/**
 * Resting background for the sticky header/footer bars — matches the table
 * chrome so the bars blend in when nothing is scrolling under them.
 */
const STICKY_BAR_BG_REST = 'bg-[oklch(0.1973_0.02_289.01)]'
/**
 * Stuck background — a notch lighter than the body rows (oklch 0.18), applied
 * only while a bar is frozen with content scrolling beneath it, so the
 * elevation reads as chrome floating above the data.
 */
const STICKY_BAR_BG_STUCK = 'bg-[oklch(0.235_0.022_289)]'
/** Downward shadow under the header, shown only while rows scroll beneath it. */
const HEADER_STUCK_SHADOW = 'shadow-[0_10px_18px_-10px_rgba(0,0,0,0.7)]'

/**
 * Light-dismiss exclusions for the open shelf: pointer-downs inside these
 * areas keep it open — the shelf itself, the chrome bar (closing on the
 * toggle would fight its own onClick), the header row (a pointer-down there
 * starts a reorder or drag-to-shelf), and any portaled header menu.
 */
const SHELF_KEEP_OPEN_SELECTOR = '[data-shelf-keep-open], [data-radix-popper-content-wrapper]'

/**
 * True while the user holds a non-collapsed text selection — i.e. the click
 * that just fired was the tail end of a copy-selection drag, not an intent
 * to activate the row.
 */
function hasTextSelection(): boolean {
  const selection = window.getSelection()
  return selection !== null && !selection.isCollapsed
}

/**
 * Pointer-first collision: a droppable physically under the pointer always
 * wins; otherwise fall back to closestCenter (which keeps chip drops over the
 * table body snapping to the nearest header). closestCenter alone measures
 * from the dragged RECT's center — with few (wide) columns, grabbing a header
 * by its right-aligned label leaves that center ~100px left of the pointer,
 * so the last header outcompeted small targets like the shelf toggle button.
 */
const pointerFirstCollision: CollisionDetection = (args) => {
  const withinPointer = pointerWithin(args)
  return withinPointer.length > 0 ? withinPointer : closestCenter(args)
}

/** View mode for the table */
type ViewMode = 'products' | 'countries'

/**
 * Performance by SKU Table component showing country summaries expandable to SKU details.
 *
 * @component
 * @example
 * ```tsx
 * <PerformanceBySkuTable className="mt-8" />
 * ```
 */
export function PerformanceBySkuTable({ className }: PerformanceBySkuTableProps): ReactElement {
  const { t } = useTranslation('features-home')
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const isBelowDesktop = useIsBelowDesktop()

  // Compute max columns based on breakpoint: mobile=3, tablet=5, desktop=7
  const maxColumns = isMobile ? 3 : isBelowDesktop ? 5 : 7

  // View mode state - products-only (default) or countries breakdown
  const [searchParams] = useSearchParams()
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const v = searchParams.get('view')
    return v === 'countries' ? 'countries' : 'products'
  })

  // Data hooks
  const { countries, isLoading: countriesLoading, isError: countriesError, error: countriesErrorMsg } = useCountryProductPerformance()
  const { products: productsOnly, isLoading: productsLoading, isError: productsError, error: productsErrorMsg } = useProductPerformance()
  const { cancellationsMap } = useProductCancellations()
  const { prefetchCountryProductQuery } = useDashboardData()

  // Derived loading/error states based on view mode
  const loading = viewMode === 'products' ? productsLoading : countriesLoading
  const isError = viewMode === 'products' ? productsError : countriesError
  const error = viewMode === 'products' ? productsErrorMsg : countriesErrorMsg

  const { getGroup } = useProductGroupsMetadata()
  const { productLabel, countryLabel } = useLabelResolver()
  const compactMoney = useCompactMoney()
  const initialExpandCountry = searchParams.get('expandCountry')
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    return initialExpandCountry ? new Set([initialExpandCountry]) : new Set()
  })
  const [activeMenuColumnId, setActiveMenuColumnId] = useState<ColumnId | null>(null)

  // Scroll-aware elevation: track whether the sticky header is currently
  // "stuck" (content scrolling beneath it) so its shadow only shows then.
  // The sentinel sits at the top of the table; when it leaves the viewport
  // (accounting for the 56px sticky offset), the bar is floating.
  const topSentinelRef = useRef<HTMLDivElement>(null)
  const [headerStuck, setHeaderStuck] = useState(false)

  useEffect(() => {
    const topEl = topSentinelRef.current
    if (!topEl) return
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry) setHeaderStuck(!entry.isIntersecting) },
      { rootMargin: '-56px 0px 0px 0px', threshold: 0 }
    )
    obs.observe(topEl)
    return () => obs.disconnect()
    // Re-attach once the table (and its sentinel) actually mounts past the
    // loading/error early-returns.
  }, [loading, isError])

  // Scroll to the initially expanded country row once data is loaded
  useEffect(() => {
    if (!initialExpandCountry || loading) return
    const el = document.querySelector(`[data-country-id="${CSS.escape(initialExpandCountry)}"]`)
    if (el) {
      requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect()
        const offset = 80 // Account for FlyingMenu sticky header
        window.scrollBy({ top: rect.top - offset, behavior: 'smooth' })
      })
    }
  }, [initialExpandCountry, loading])

  const [columnFilters, setColumnFilters] = useState<Record<string, { value: number; direction: 'lt' | 'gt' }>>({})

  const columnMaxValues = useMemo(() => {
    // Use products from either view mode for column max calculation
    const allProducts = viewMode === 'products'
      ? productsOnly
      : countries?.flatMap(c => c.products) || []

    if (allProducts.length === 0) return {}
    const values: Record<string, number[]> = {}

    // Collect all values
    allProducts.forEach(p => {
      if (!values['quantity']) values['quantity'] = []
      values['quantity'].push(p.quantity)

      if (!values['sales']) values['sales'] = []
      values['sales'].push(p.sales)

      if (!values['profit']) values['profit'] = []
      values['profit'].push(p.profit)

      if (!values['adSpend']) values['adSpend'] = []
      values['adSpend'].push(p.adSpend)

      // For other columns, just track max directly or collect if we want outliers logic later
      // Keeping simple max for others for now
    })

    const maxes: Record<string, number> = {}

    // Helper to get max excluding top 3 outliers
    const getMaxExcludingOutliers = (arr: number[]): number => {
      if (!arr || arr.length === 0) return 0
      if (arr.length <= 3) return Math.max(...arr)
      // Sort descending
      const sorted = [...arr].sort((a, b) => b - a)
      // Return the 4th largest value (index 3) as the "max" for the slider
      // This clips the top 3 extreme values
      return sorted[3] ?? 0
    }

    maxes['quantity'] = getMaxExcludingOutliers(values['quantity'] || [])
    maxes['sales'] = getMaxExcludingOutliers(values['sales'] || [])
    maxes['profit'] = getMaxExcludingOutliers(values['profit'] || [])
    maxes['adSpend'] = getMaxExcludingOutliers(values['adSpend'] || [])

    // Standard max for others
    const checkMax = (key: string, val: number): void => {
      if (val > (maxes[key] || 0)) maxes[key] = val
    }

    allProducts.forEach(p => {
      // checkMax('adSpend', p.adSpend) // Handled with outliers logic now
      checkMax('googleAds', getPlatformSpend(p.adPlatformBreakdown, ['google', 'googleads']))
      checkMax('metaAds', getPlatformSpend(p.adPlatformBreakdown, ['meta', 'facebook', 'fb', 'instagram']))
      checkMax('cogs', p.cogs)
      checkMax('totalExpenses', p.totalExpenses)
      checkMax('roas', p.roas)
      checkMax('margin', p.margin)
    })
    
    // Add buffer to max values so slider isn't always at 100%
    Object.keys(maxes).forEach(k => {
      const current = maxes[k] || 0
      if (current === 0) maxes[k] = 100 // Default for empty
      else maxes[k] = current
    })

    // Cap ROAS at 30 to avoid outliers ruining the slider scale
    if (maxes['roas'] && maxes['roas'] > 30) {
      maxes['roas'] = 30
    }

    return maxes
  }, [viewMode, productsOnly, countries])

  type SortDirection = 'asc' | 'desc'
  interface SortConfig {
    key: ColumnId
    direction: SortDirection
  }
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(() => {
    const urlSort = searchParams.get('sort')
    if (urlSort) {
      const [key, dir] = urlSort.split(':')
      if (DEFAULT_COLUMN_ORDER.includes(key as ColumnId) && (dir === 'asc' || dir === 'desc')) {
        return { key: key as ColumnId, direction: dir as SortDirection }
      }
    }
    return null
  })

  // Column visibility and ordering state (persisted to localStorage)
  const {
    columnOrder,
    toggleColumn,
    isVisible,
    handleDragEnd,
    isAtMax,
    maxVisible: currentMaxVisible,
    setVisibleColumns,
    showColumnAt,
  } = useTableColumnState<ColumnId>({
    storageKey: COLUMN_STATE_STORAGE_KEY,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    defaultOrder: DEFAULT_COLUMN_ORDER,
    maxVisible: maxColumns,
  })

  // Column Shelf (desktop only): open state persisted, drag wiring for chips.
  const [shelfOpen, setShelfOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem(SHELF_OPEN_STORAGE_KEY) === 'true'
  })
  useEffect(() => {
    try {
      localStorage.setItem(SHELF_OPEN_STORAGE_KEY, String(shelfOpen))
    } catch (e) {
      console.error('Failed to save shelf open state to localStorage', e)
    }
  }, [shelfOpen])

  // Light-dismiss: a pointer-down anywhere outside the shelf's working set
  // (see SHELF_KEEP_OPEN_SELECTOR) closes the open shelf.
  useEffect(() => {
    if (!shelfOpen || isBelowDesktop) return
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target
      if (target instanceof Element && target.closest(SHELF_KEEP_OPEN_SELECTOR) === null) {
        setShelfOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [shelfOpen, isBelowDesktop])

  const appendColumn = useCallback(
    (columnId: string): void => {
      showColumnAt(columnId as ColumnId, columnOrder.length)
    },
    [showColumnAt, columnOrder.length]
  )

  const {
    activeChipColumn,
    activeHeaderColumn,
    isShelfDropTarget,
    dropIndicator,
    onDragStart,
    onDragOver,
    onDragMove,
    onDragEnd,
    onDragCancel,
  } = useColumnShelfDnd<ColumnId>({
    columnOrder,
    showColumnAt,
    hideColumn: toggleColumn,
    reorder: handleDragEnd,
  })

  // URL param presets: ?columns=sales,profit,margin&view=countries
  // One-time override of persisted column visibility from URL deep-link
  const appliedUrlPreset = useRef(false)
  useEffect(() => {
    if (appliedUrlPreset.current) return
    appliedUrlPreset.current = true

    const urlColumns = searchParams.get('columns')
    if (urlColumns) {
      const cols = urlColumns.split(',').filter((c): c is ColumnId =>
        DEFAULT_COLUMN_ORDER.includes(c as ColumnId) && c !== 'country'
      )
      if (cols.length > 0) {
        setVisibleColumns(new Set(cols.slice(0, maxColumns)))
      }
    }
  }, [searchParams, setVisibleColumns, maxColumns])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const getValue = useCallback((item: CountryTotals | SkuMetrics, key: ColumnId): number | string => {
    if (key === 'googleAds') return getPlatformSpend(item.adPlatformBreakdown, ['google', 'googleads'])
    if (key === 'metaAds') return getPlatformSpend(item.adPlatformBreakdown, ['meta', 'facebook', 'fb', 'instagram'])
    if (key === 'country') return (item as CountryTotals).name || (item as unknown as { country: string }).country || ''

    // Safe cast because we know ColumnId excludes complex objects like adPlatformBreakdown
    // and we've handled special cases above.
    return (item as unknown as Record<string, number | string>)[key] ?? 0
  }, [])

  // Compute country totals from the new derived hook
  const countryTotals = useMemo((): CountryTotals[] => {
    if (!countries) return []

    const mapped = countries.map(country => ({
      id: country.country,
      name: country.country,
      code: country.country,
      quantity: country.quantity,
      sales: country.sales,
      adSpend: country.adSpend,
      adPlatformBreakdown: country.adPlatformBreakdown,
      cogs: country.cogs,
      totalExpenses: country.totalExpenses,
      profit: country.profit,
      roas: country.roas,
      margin: country.margin,
    }))

    if (sortConfig) {
      return mapped.sort((a, b) => {
        const valA = getValue(a, sortConfig.key) as number
        const valB = getValue(b, sortConfig.key) as number
        return sortConfig.direction === 'asc' ? valA - valB : valB - valA
      })
    }

    return mapped
  }, [countries, sortConfig, getValue])

  // Compute SKU metrics for expanded countries
  const skuMetrics = useMemo((): Record<string, SkuMetrics[]> => {
    if (!countries) return {}

    const result: Record<string, SkuMetrics[]> = {}
    countries.forEach(country => {
      const countryId = country.country
      let products = country.products.map(product => ({
        sku: product.sku,
        quantity: product.quantity,
        sales: product.sales,
        adSpend: product.adSpend,
        adPlatformBreakdown: product.adPlatformBreakdown,
        cogs: product.cogs,
        totalExpenses: product.totalExpenses,
        profit: product.profit,
        roas: product.roas,
        margin: product.margin,
      }))

      // Apply filters
      products = products.filter(item => {
        for (const [key, filter] of Object.entries(columnFilters)) {
          const val = getValue(item, key as ColumnId)
          if (typeof val === 'number') {
             if (filter.direction === 'lt' && val > filter.value) return false
             if (filter.direction === 'gt' && val < filter.value) return false
          }
        }
        return true
      })

      if (sortConfig) {
        products.sort((a, b) => {
          const valA = getValue(a, sortConfig.key) as number
          const valB = getValue(b, sortConfig.key) as number
          return sortConfig.direction === 'asc' ? valA - valB : valB - valA
        })
      }

      result[countryId] = products
    })
    return result
  }, [countries, sortConfig, getValue, columnFilters])

  // Compute product metrics for products-only view (with filters and sorting)
  const productMetrics = useMemo((): SkuMetrics[] => {
    if (!productsOnly) return []

    let products = productsOnly.map(product => ({
      sku: product.sku,
      quantity: product.quantity,
      sales: product.sales,
      adSpend: product.adSpend,
      adPlatformBreakdown: product.adPlatformBreakdown,
      cogs: product.cogs,
      totalExpenses: product.totalExpenses,
      profit: product.profit,
      roas: product.roas,
      margin: product.margin,
    }))

    // Apply filters
    products = products.filter(item => {
      for (const [key, filter] of Object.entries(columnFilters)) {
        const val = getValue(item, key as ColumnId)
        if (typeof val === 'number') {
          if (filter.direction === 'lt' && val > filter.value) return false
          if (filter.direction === 'gt' && val < filter.value) return false
        }
      }
      return true
    })

    if (sortConfig) {
      products.sort((a, b) => {
        const valA = getValue(a, sortConfig.key) as number
        const valB = getValue(b, sortConfig.key) as number
        return sortConfig.direction === 'asc' ? valA - valB : valB - valA
      })
    }

    return products
  }, [productsOnly, sortConfig, getValue, columnFilters])

  // Filter-aware totals for the sticky footer.
  // Products view: aggregate the filtered product rows.
  // Countries view: aggregate the displayed country rows (column filters only
  // hide expanded SKU sub-rows there, not the country totals themselves).
  const totalsRow = useMemo((): TotalsRow => {
    const source = viewMode === 'products' ? productMetrics : countryTotals
    return computeTotals(source)
  }, [viewMode, productMetrics, countryTotals])

  // Toggle expansion state
  const toggleExpanded = useCallback((countryId: string): void => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(countryId)) {
        next.delete(countryId)
      } else {
        next.add(countryId)
      }
      return next
    })
  }, [])

  // Handle keyboard interaction
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, countryId: string) => {
      if (event.key === 'Enter' || event.key === ' ' ) {
        event.preventDefault()
        toggleExpanded(countryId)
      }
    },
    [toggleExpanded]
  )

  if (loading) return <LoadingState />
  if (isError && error) return <ErrorState message={error} />

  // Check empty state based on view mode
  const hasData = viewMode === 'products'
    ? productsOnly && productsOnly.length > 0
    : countries && countries.length > 0
  if (!hasData) return <EmptyState />

  const columnsConfig: { id: ColumnId; label: string }[] = [
    { id: 'sales', label: t('tables.performanceBySku.columns.sales') },
    { id: 'adSpend', label: t('tables.performanceBySku.columns.adSpend') },
    { id: 'googleAds', label: 'Google Ads' },
    { id: 'metaAds', label: 'Meta Ads' },
    { id: 'cogs', label: t('tables.performanceBySku.columns.costOfGoods') },
    { id: 'totalExpenses', label: t('tables.performanceBySku.columns.totalExpenses') },
    { id: 'profit', label: t('tables.performanceBySku.columns.profit') },
    { id: 'roas', label: t('tables.performanceBySku.columns.roas') },
    { id: 'margin', label: t('tables.performanceBySku.columns.profitMargin') },
  ]

  // Live grand totals for shelf chips — totalsRow already computes every
  // metric (visible or not), so hidden metrics are free to display.
  const chipValue = (id: ColumnId): React.ReactNode => {
    switch (id) {
      case 'sales':
        return compactMoney(totalsRow.sales)
      case 'adSpend':
        return compactMoney(totalsRow.adSpend)
      case 'googleAds':
        return compactMoney(getPlatformSpend(totalsRow.adPlatformBreakdown, ['google', 'googleads']))
      case 'metaAds':
        return compactMoney(
          getPlatformSpend(totalsRow.adPlatformBreakdown, ['meta', 'facebook', 'fb', 'instagram'])
        )
      case 'cogs':
        return compactMoney(totalsRow.cogs)
      case 'totalExpenses':
        return compactMoney(totalsRow.totalExpenses)
      case 'profit':
        return (
          <span className={totalsRow.profit >= 0 ? 'text-positive' : 'text-negative'}>
            {totalsRow.profit >= 0 ? '+' : ''}
            {compactMoney(totalsRow.profit)}
          </span>
        )
      case 'roas':
        return `${totalsRow.roas.toFixed(2)}×`
      case 'margin':
        return formatPercent(totalsRow.margin)
      default:
        return null
    }
  }

  const shelfSections = COLUMN_GROUPS.map(group => ({
    id: group.id,
    label: t(`tables.performanceBySku.shelf.groups.${group.id}`, {
      defaultValue: group.defaultLabel,
    }),
    chips: columnsConfig
      .filter(column => group.columns.includes(column.id) && !isVisible(column.id))
      .map(column => ({
        id: column.id,
        label: column.label.replace(/\n/g, ' '),
        value: chipValue(column.id),
      })),
  })).filter(section => section.chips.length > 0)

  // Determine the last visible column (to conditionally hide its right border)
  const lastVisibleColumnId = columnOrder.filter(id => id !== 'country' && isVisible(id)).at(-1)

  const renderHeaderContent = (id: ColumnId): React.ReactNode => {
    const maxValue = columnMaxValues[id] || 100
    const dropEdge = dropIndicator?.columnId === id ? dropIndicator.edge : null
    const isNumeric = true // Assuming all sortable columns are numeric for now

    const handleFilter = (val: number, direction: 'lt' | 'gt'): void => {
      setColumnFilters(prev => ({ ...prev, [id]: { value: val, direction } }))
    }

    const handleClearFilter = (): void => {
      setColumnFilters(prev => {
        const next = { ...prev }
        delete next[id]
        return next
      })
    }

    const handleSortAsc = (): void => {
      setSortConfig({ key: id, direction: 'asc' })
    }

    const handleSortDesc = (): void => {
      setSortConfig({ key: id, direction: 'desc' })
    }

    const currentSortDir = sortConfig?.key === id ? sortConfig.direction : null

    let formatValue: (val: number) => React.ReactNode = (val) => formatInt(val)
    if (['sales', 'adSpend', 'googleAds', 'metaAds', 'cogs', 'totalExpenses', 'profit'].includes(id)) {
        formatValue = (val: number) => compactMoney(val)
    } else if (id === 'margin') {
        formatValue = (val: number) => formatPercent(val)
    } else if (id === 'roas') {
        formatValue = (val: number) => `${val.toFixed(2)} ROAS`
    }

    const renderHeaderWithMenu = (title: React.ReactNode, _className?: string, align: 'start' | 'end' | 'center' = 'start'): ReactElement => {
      return (
        <TableHeaderMenu
          title={title}
          subtitle={chipValue(id)}
          onSortAsc={handleSortAsc}
          onSortDesc={handleSortDesc}
          sortDirection={currentSortDir}
          onFilter={handleFilter}
          onClearFilter={handleClearFilter}
          currentFilter={columnFilters[id]}
          maxValue={maxValue}
          isNumeric={isNumeric}
          // Right padding mirrors the body cells (p-4 / px-2) so header text
          // lines up with the column values beneath it.
          className={isMobile ? "pl-1 pr-2 py-1 text-[10px]" : "pl-2 pr-4 py-3"}
          open={activeMenuColumnId === id}
          onOpenChange={(open) => setActiveMenuColumnId(open ? id : null)}
          align={align}
          formatValue={formatValue}
        />
      )
    }

    switch (id) {
      case 'country':
        return (
          <TableHead className={cn(
            "text-left font-medium sticky left-0 z-20 transition-colors duration-200",
            headerStuck ? STICKY_BAR_BG_STUCK : STICKY_BAR_BG_REST,
            isMobile ? "px-2 py-2 w-14 min-w-14 max-w-14 text-xs" : "px-4 py-3 w-1/4 min-w-44 max-w-1/4"
          )}>
            {!isMobile && (viewMode === 'products'
              ? t('tables.performanceBySku.columns.name', { defaultValue: 'Name' })
              : t('tables.performanceBySku.columns.country'))}
            {/* Grand totals live in the headers (the sticky footer is gone) —
                the row count anchors them under the first column's label. */}
            <div className="font-mono text-xs font-normal tabular-nums text-muted-foreground/60">
              {!isMobile && (
                <span className="text-muted-foreground/50">
                  {t('tables.performanceBySku.totalsLabel', { defaultValue: 'Total' })}{' '}
                </span>
              )}
              {formatInt(totalsRow.quantity)} ×
            </div>
          </TableHead>
        )
      case 'sales':
        return (
          <DraggableTableHead
            id={id}
            dropEdge={dropEdge}
            className="p-0 text-right font-medium transition-colors select-none"
            currentFilter={columnFilters[id]}
            onClearFilter={handleClearFilter}
            formatValue={formatValue}
          >
            {renderHeaderWithMenu(
              t('tables.performanceBySku.columns.sales'),
              "px-2 py-3",
              "end"
            )}
          </DraggableTableHead>
        )
      case 'adSpend':
        return (
          <DraggableTableHead
            id={id}
            dropEdge={dropEdge}
            className="p-0 text-right font-medium transition-colors select-none"
            currentFilter={columnFilters[id]}
            onClearFilter={handleClearFilter}
            formatValue={formatValue}
          >
            {renderHeaderWithMenu(
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="block truncate">
                    {t('tables.performanceBySku.columns.adSpend')
                      .split('\n')
                      .map((line, i) => (
                        <span key={i}>{line} </span>
                      ))}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-xs">
                    <div className="font-medium mb-1">
                      {t('tables.performanceBySku.tooltips.adSpendBreakdown')}
                    </div>
                    <div>{t('tables.performanceBySku.tooltips.googleMetaAds')}</div>
                  </div>
                </TooltipContent>
              </Tooltip>,
              "px-2 py-3",
              "end"
            )}
          </DraggableTableHead>
        )
      case 'googleAds':
        return (
          <DraggableTableHead
            id={id}
            dropEdge={dropEdge}
            className="p-0 text-right font-medium transition-colors select-none"
            currentFilter={columnFilters[id]}
            onClearFilter={handleClearFilter}
            formatValue={formatValue}
          >
            {renderHeaderWithMenu(
              "Google Ads",
              "px-2 py-3",
              "end"
            )}
          </DraggableTableHead>
        )
      case 'metaAds':
        return (
          <DraggableTableHead
            id={id}
            dropEdge={dropEdge}
            className="p-0 text-right font-medium transition-colors select-none"
            currentFilter={columnFilters[id]}
            onClearFilter={handleClearFilter}
            formatValue={formatValue}
          >
            {renderHeaderWithMenu(
              "Meta Ads",
              "px-2 py-3",
              "end"
            )}
          </DraggableTableHead>
        )
      case 'cogs':
        return (
          <DraggableTableHead
            id={id}
            dropEdge={dropEdge}
            className="p-0 text-right font-medium transition-colors select-none"
            currentFilter={columnFilters[id]}
            onClearFilter={handleClearFilter}
            formatValue={formatValue}
          >
            {renderHeaderWithMenu(
              t('tables.performanceBySku.columns.costOfGoods').replace(/\n/g, ' '),
              "px-2 py-3",
              "end"
            )}
          </DraggableTableHead>
        )
      case 'totalExpenses':
        return (
          <DraggableTableHead
            id={id}
            dropEdge={dropEdge}
            className="p-0 text-right font-medium transition-colors select-none"
            currentFilter={columnFilters[id]}
            onClearFilter={handleClearFilter}
            formatValue={formatValue}
          >
            {renderHeaderWithMenu(
              t('tables.performanceBySku.columns.totalExpenses').replace(/\n/g, ' '),
              "px-2 py-3",
              "end"
            )}
          </DraggableTableHead>
        )
      case 'profit':
        return (
          <DraggableTableHead
            id={id}
            dropEdge={dropEdge}
            className="p-0 text-right font-medium transition-colors select-none"
            currentFilter={columnFilters[id]}
            onClearFilter={handleClearFilter}
            formatValue={formatValue}
          >
            {renderHeaderWithMenu(
              t('tables.performanceBySku.columns.profit'),
              "px-2 py-3",
              "end"
            )}
          </DraggableTableHead>
        )
      case 'roas':
        return (
          <DraggableTableHead
            id={id}
            dropEdge={dropEdge}
            className="p-0 text-right font-medium transition-colors select-none"
            currentFilter={columnFilters[id]}
            onClearFilter={handleClearFilter}
            formatValue={formatValue}
          >
            {renderHeaderWithMenu(
              t('tables.performanceBySku.columns.roas'),
              "px-2 py-3",
              "end"
            )}
          </DraggableTableHead>
        )
      case 'margin':
        return (
          <DraggableTableHead
            id={id}
            dropEdge={dropEdge}
            className="p-0 text-right font-medium transition-colors select-none"
            currentFilter={columnFilters[id]}
            onClearFilter={handleClearFilter}
            formatValue={formatValue}
          >
            {renderHeaderWithMenu(
              t('tables.performanceBySku.columns.profitMargin').replace(/\n/g, ' '),
              "pl-2 pr-3 py-3",
              "end"
            )}
          </DraggableTableHead>
        )
      default:
        return null
    }
  }

  const renderCellContent = (id: ColumnId, item: CountryTotals | SkuMetrics | TotalsRow, _type: 'country' | 'sku', isBlurred?: boolean, _isLast?: boolean): React.ReactNode => {
    const blurClass = isBlurred ? "blur-[1px] opacity-50" : "";
    const cellClass = cn(
      "text-right font-mono whitespace-nowrap",
      isMobile ? "px-2 py-2 text-xs" : "p-4"
    )

    switch (id) {
      case 'sales':
        return (
          <TableCell className={cellClass}>
            <span className={cn(blurClass, "text-white/90")}>
              {compactMoney(item.sales)}
            </span>
          </TableCell>
        )
      case 'adSpend':
        return (
          <TableCell className={cellClass}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={cn("cursor-help text-muted-foreground", blurClass)}>
                  {compactMoney(item.adSpend)}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs space-y-1">
                  <div className="font-medium">
                    {t('tables.performanceBySku.tooltips.adSpendBreakdown')}
                  </div>
                  {Object.entries(item.adPlatformBreakdown).length > 0 ? (
                    Object.entries(item.adPlatformBreakdown).map(
                      ([platform, amount]) => (
                        <div key={platform} className="flex justify-between gap-2">
                          <span>{platform}:</span>
                          <span className="font-mono">
                            {compactMoney(amount)}
                          </span>
                        </div>
                      )
                    )
                  ) : (
                    <div className="text-muted-foreground">
                      {t('tables.performanceBySku.tooltips.noBreakdownAvailable')}
                    </div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TableCell>
        )
      case 'googleAds':
        return (
          <TableCell className={cellClass}>
            <span className={cn(blurClass, "text-muted-foreground")}>
              {compactMoney(getPlatformSpend(item.adPlatformBreakdown, ['google', 'googleads']))}
            </span>
          </TableCell>
        )
      case 'metaAds':
        return (
          <TableCell className={cellClass}>
            <span className={cn(blurClass, "text-muted-foreground")}>
              {compactMoney(getPlatformSpend(item.adPlatformBreakdown, ['meta', 'facebook', 'fb', 'instagram']))}
            </span>
          </TableCell>
        )
      case 'cogs':
        return (
          <TableCell className={cellClass}>
            <span className={cn(blurClass, "text-muted-foreground")}>
              {compactMoney(item.cogs)}
            </span>
          </TableCell>
        )
      case 'totalExpenses':
        return (
          <TableCell className={cellClass}>
            <span className={cn(blurClass, "text-muted-foreground")}>
              {compactMoney(item.totalExpenses)}
            </span>
          </TableCell>
        )
      case 'profit':
        return (
          <TableCell className={cellClass}>
            <span className={cn(
              "font-bold",
              blurClass,
              item.profit >= 0 ? 'text-positive' : 'text-negative'
            )}>
              {item.profit >= 0 ? '+' : ''}{compactMoney(item.profit)}
            </span>
          </TableCell>
        )
      case 'roas':
        return (
          <TableCell className={cellClass}>
            <span className={cn(blurClass, "text-muted-foreground")}>
              {item.roas.toFixed(2)}×
            </span>
          </TableCell>
        )
      case 'margin':
        return (
          <TableCell className={cellClass}>
            <span className={cn(blurClass, "text-muted-foreground")}>
              {formatPercent(item.margin)}
            </span>
          </TableCell>
        )
      default:
        return null
    }
  }

  // Products/Countries switch. Bar mode (desktop) renders it as flush tab
  // segments inside the card's chrome bar: transparent over the bar surface,
  // right-edge dividers only (the bar draws the bottom hairline), and the
  // wrapper's variant-scoped first:border-l cancelled with the same
  // data-[variant=outline] prefix so the card edge supplies that line.
  const renderViewToggle = (inBar: boolean): ReactElement => (
    <ToggleGroup
      type="single"
      value={viewMode}
      onValueChange={(value) => value && setViewMode(value as ViewMode)}
      variant="outline"
      size="lg"
      aria-label={t('tables.performanceBySku.viewToggle.label')}
      onMouseEnter={prefetchCountryProductQuery}
      className={cn(inBar && 'rounded-none')}
    >
      {(['products', 'countries'] as const).map(mode => (
        <ToggleGroupItem
          key={mode}
          value={mode}
          aria-label={t(`tables.performanceBySku.viewToggle.${mode}`)}
          className={cn(
            'px-5 text-base font-semibold text-foreground/70 data-[state=on]:text-foreground data-[state=on]:bg-foreground/10 data-[state=on]:shadow-inner',
            inBar &&
              'h-10 bg-transparent rounded-none first:rounded-l-none last:rounded-r-none border-0 border-r border-border data-[variant=outline]:first:border-l-0'
          )}
        >
          {t(`tables.performanceBySku.viewToggle.${mode}`)}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  )

  return (
    <div className={cn("rounded-2xl border border-border overflow-clip", className)}
      style={{ background: 'oklch(0.23 0.023 292 / 0.8)' }}
    >
      {/* DndContext spans the chrome bar too — the shelf toggle button is a
          header drop zone (drop to hide while the shelf is closed). */}
      <DndContext
        sensors={sensors}
        collisionDetection={pointerFirstCollision}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
      {/* Desktop: a chrome bar — darker surface, full-width hairline — so the
          tab strip reads as card furniture, one rank above the data header. */}
      <div data-shelf-keep-open className={cn(
        'flex items-center justify-between',
        isMobile
          ? 'px-3 pt-3 pb-2'
          : isBelowDesktop
            ? 'px-6 pt-6 pb-4'
            : 'h-10 border-b border-white/15 bg-black/20'
      )}>
        {/* View mode toggle as title */}
        {isMobile ? (
          <Button
            variant="outline"
            size="lg"
            className="px-5 text-base font-semibold"
            onClick={() => {
              setViewMode(prev => prev === 'products' ? 'countries' : 'products')
              prefetchCountryProductQuery()
            }}
            aria-label={t('tables.performanceBySku.viewToggle.label')}
          >
            {viewMode === 'products'
              ? t('tables.performanceBySku.viewToggle.products')
              : t('tables.performanceBySku.viewToggle.countries')}
            <ArrowLeftRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          renderViewToggle(!isBelowDesktop)
        )}
        <div className="flex items-center gap-2 ml-auto">
          {Object.keys(columnFilters).length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-3 text-muted-foreground hover:text-foreground"
              onClick={() => setColumnFilters({})}
            >
              <FilterX className="mr-2 h-4 w-4" />
              {t('tables.performanceBySku.clearFilters', { defaultValue: 'Clear filters' })}
            </Button>
          )}
          {isBelowDesktop ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size={isMobile ? 'icon' : 'default'}>
                  {isMobile ? (
                    <MoreVertical className="h-4 w-4" />
                  ) : (
                    <>
                      {t('tables.performanceBySku.columns.label', { defaultValue: 'Columns' })} <ChevronDown className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {columnsConfig.slice(0, 2).map(column => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className={cn("capitalize", !isVisible(column.id) && "text-muted-foreground")}
                    checked={isVisible(column.id)}
                    onCheckedChange={() => toggleColumn(column.id)}
                    onSelect={(e) => e.preventDefault()}
                    disabled={isAtMax && !isVisible(column.id)}
                  >
                    {column.label.replace(/\n/g, ' ')}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                {columnsConfig.slice(2, 5).map(column => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className={cn("capitalize", !isVisible(column.id) && "text-muted-foreground")}
                    checked={isVisible(column.id)}
                    onCheckedChange={() => toggleColumn(column.id)}
                    onSelect={(e) => e.preventDefault()}
                    disabled={isAtMax && !isVisible(column.id)}
                  >
                    {column.label.replace(/\n/g, ' ')}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                {columnsConfig.slice(5, 7).map(column => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className={cn("capitalize", !isVisible(column.id) && "text-muted-foreground")}
                    checked={isVisible(column.id)}
                    onCheckedChange={() => toggleColumn(column.id)}
                    onSelect={(e) => e.preventDefault()}
                    disabled={isAtMax && !isVisible(column.id)}
                  >
                    {column.label.replace(/\n/g, ' ')}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                {columnsConfig.slice(7).map(column => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className={cn("capitalize", !isVisible(column.id) && "text-muted-foreground")}
                    checked={isVisible(column.id)}
                    onCheckedChange={() => toggleColumn(column.id)}
                    onSelect={(e) => e.preventDefault()}
                    disabled={isAtMax && !isVisible(column.id)}
                  >
                    {column.label.replace(/\n/g, ' ')}
                  </DropdownMenuCheckboxItem>
                ))}
                {currentMaxVisible !== undefined && (
                  <>
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1.5 text-xs text-muted-foreground text-center">
                      {currentMaxVisible} {t('tables.performanceBySku.columnsMax', { defaultValue: 'columns max' })}
                    </div>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <ShelfToggleDropButton
              shelfOpen={shelfOpen}
              isDragActive={activeHeaderColumn !== null}
              onToggle={() => setShelfOpen(prev => !prev)}
              label={t('tables.performanceBySku.columns.label', { defaultValue: 'Columns' })}
            />
          )}
        </div>
      </div>
        <div className="flex">
          <div className="min-w-0 flex-1 overflow-x-clip">
            {/* Top sentinel: leaves the viewport (offset by the 56px sticky top) when the header sticks */}
            <div ref={topSentinelRef} aria-hidden="true" className="h-0" />
          {/* table-fixed on every breakpoint: the wrapper clips instead of
              scrolling (to keep page-level sticky), so the layout must be
              GUARANTEED to fit — auto table layout overflows in edge cases
              (e.g. 7 columns + open shelf). Fixed: name column 25%, metric
              columns split the rest evenly. */}
          <table className={cn("w-full caption-bottom text-foreground table-fixed", isMobile ? "text-xs" : "text-sm")}>
            <thead data-shelf-keep-open className={cn(
              "sticky top-14 z-20 transition-[background-color,box-shadow] duration-200",
              headerStuck ? STICKY_BAR_BG_STUCK : STICKY_BAR_BG_REST,
              headerStuck && HEADER_STUCK_SHADOW
            )}>
              <TableRow className="hover:bg-transparent border-0 border-b border-white/15">
                {/* Static Country Column */}
                {renderHeaderContent('country')}
                
                <SortableContext
                  items={columnOrder.filter(id => id !== 'country' && isVisible(id))}
                  strategy={horizontalListSortingStrategy}
                >
                  {columnOrder.map(columnId => {
                    if (columnId === 'country' || !isVisible(columnId)) return null
                    return (
                      <React.Fragment key={columnId}>
                        {renderHeaderContent(columnId)}
                      </React.Fragment>
                    )
                  })}
                </SortableContext>
                {!isBelowDesktop && lastVisibleColumnId === undefined && (
                  <EmptyColumnsDropHead
                    label={t('tables.performanceBySku.shelf.dropHere', {
                      defaultValue: 'Drop a column here',
                    })}
                  />
                )}
                              </TableRow>
            </thead>
            <TableBody>
            {viewMode === 'products' ? (
              /* Products-only view */
              productMetrics.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={1 + columnOrder.filter(c => c !== 'country' && isVisible(c)).length} className="h-24 text-center text-muted-foreground">
                    {t('tables.performanceBySku.noResults', { defaultValue: 'No results match your filters.' })}
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {productMetrics.map((product, index) => {
                    const displayName = productLabel(product.sku)
                    const group = getGroup(product.sku)
                    const imageUrl = group?.imageUrl ?? null
                    const cancellation = cancellationsMap.get(product.sku)

                    return (
                      <TableRow
                        key={`product-${product.sku}-${index}`}
                        className={cn(
                          "group transition-colors bg-[oklch(0.18_0.016_290)] hover:bg-white/5",
                          isMobile && "cursor-pointer",
                          index > 0 && "border-t border-white/5"
                        )}
                        // Desktop: navigation lives on the first-column Link only,
                        // so clicks (and copy-selections) on metric cells are inert.
                        // Mobile keeps the whole-row tap — its first cell is just a
                        // thumbnail popover.
                        onClick={isMobile ? () => {
                          // Finishing a copy-selection must not navigate away.
                          if (hasTextSelection()) return
                          navigate(`/dashboard/product/${encodeURIComponent(product.sku)}`, {
                            state: {
                              memberSkus: group?.members ?? [],
                              productName: displayName,
                              imageUrl,
                            },
                          })
                        } : undefined}
                      >
                        <TableCell className={cn(
                          "text-left",
                          // min-w-44 (not 25%) so the name column can compress
                          // when 6-7 metric columns are visible; max-w-sm on the
                          // content caps the column's preferred width so long
                          // names wrap instead of widening the column.
                          isMobile ? "p-2 w-14 min-w-14 max-w-14" : "p-4 w-1/4 min-w-44 max-w-1/4"
                        )}>
                          <div className="flex items-start gap-3 min-w-0 max-w-sm">
                            {isMobile ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button type="button" className="shrink-0 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-md">
                                    <Thumbnail alt={displayName} src={imageUrl} size={36} className="rounded-md ring-1 ring-border/50" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent side="bottom" align="start" className="w-auto max-w-[200px] p-3">
                                  <div className="flex flex-col gap-1">
                                    <span className="text-sm font-medium leading-tight text-foreground">
                                      {displayName}
                                    </span>
                                    <span className="text-muted-foreground text-xs tabular-nums">
                                      {formatInt(product.quantity)} units
                                    </span>
                                    {cancellation && cancellation.quantity > 0 && (
                                      <div className="mt-1">
                                        <ReturnsIndicator
                                          quantity={cancellation.quantity}
                                          potentialRevenueLoss={cancellation.potentialRevenueLoss}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              /* The whole name block is the navigation hitbox — a real
                                 Link, so middle/cmd-click opens stats in a new tab.
                                 Metric cells stay inert for safe copy-selection. */
                              <Link
                                to={`/dashboard/product/${encodeURIComponent(product.sku)}`}
                                state={{
                                  memberSkus: group?.members ?? [],
                                  productName: displayName,
                                  imageUrl,
                                }}
                                onClick={(e) => {
                                  // Finishing a copy-selection on the name must not navigate.
                                  if (hasTextSelection()) e.preventDefault()
                                }}
                                className="flex items-start gap-3 min-w-0"
                                title={displayName}
                              >
                                <Thumbnail alt={displayName} src={imageUrl} size={40} className="shrink-0 rounded-md ring-1 ring-border/50" />
                                <div className="flex flex-col gap-0.5 min-w-0 justify-center min-h-10">
                                  {/* The indicator sits beside (not inside) the clamped
                                      span — inline-flex on the span would override
                                      line-clamp's -webkit-box display and kill wrapping. */}
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    {/* whitespace-normal: the TableCell base sets nowrap,
                                        which would defeat line-clamp's wrapping */}
                                    <span className="text-sm font-medium leading-tight line-clamp-2 whitespace-normal text-white/90">
                                      {displayName}
                                    </span>
                                    {cancellation && cancellation.quantity > 0 && (
                                      <ReturnsIndicator
                                        quantity={cancellation.quantity}
                                        potentialRevenueLoss={cancellation.potentialRevenueLoss}
                                      />
                                    )}
                                  </div>
                                  <span className="text-muted-foreground text-xs font-light tabular-nums">
                                    {formatInt(product.quantity)} ×
                                  </span>
                                </div>
                              </Link>
                            )}
                          </div>
                        </TableCell>
                        {columnOrder.map(columnId => {
                          if (columnId === 'country' || !isVisible(columnId)) return null
                          const isLast = columnId === lastVisibleColumnId
                          return (
                            <React.Fragment key={columnId}>
                              {renderCellContent(columnId, product, 'country', false, isLast)}
                            </React.Fragment>
                          )
                        })}
                      </TableRow>
                    )
                  })}
                </>
              )
            ) : (
              /* Countries breakdown view */
              countryTotals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={1 + columnOrder.filter(c => c !== 'country' && isVisible(c)).length} className="h-24 text-center text-muted-foreground">
                    {t('tables.performanceBySku.noResults', { defaultValue: 'No results match your filters.' })}
                  </TableCell>
                </TableRow>
              ) : (
                countryTotals.map((country, countryIndex) => {
                  const isExpanded = expanded.has(country.id)
                  const skus = skuMetrics[country.id] || []

                  return (
                    <React.Fragment key={country.id}>
                      {/* Country Summary Row */}
                      <TableRow
                        data-country-id={country.id}
                        className={cn(
                          "group cursor-pointer bg-[oklch(0.18_0.016_290)] hover:bg-white/5 transition-colors",
                          countryIndex > 0 && "border-t border-white/5"
                        )}
                        onClick={() => {
                          if (hasTextSelection()) return
                          toggleExpanded(country.id)
                        }}
                        onKeyDown={(e) => handleKeyDown(e, country.id)}
                        tabIndex={0}
                        role="button"
                        aria-expanded={isExpanded}
                        aria-controls={`skus-${country.id}`}
                      >
                        <TableCell
                          className={cn(
                            "text-left relative",
                            isMobile ? "p-2 w-14 min-w-14 max-w-14" : "p-4 w-1/4 min-w-44 max-w-1/4"
                          )}
                          scope="row"
                        >
                          <div className={cn("flex items-center min-w-0", isMobile ? "gap-1" : "gap-2")}>
                            {skus.length > 0 && !isMobile && (
                              <span
                                className={cn(
                                  'text-muted-foreground transition-transform duration-200',
                                  isExpanded && 'rotate-90'
                                )}
                                aria-expanded={isExpanded}
                              >
                                <ChevronDown className="h-4 w-4 -rotate-90" />
                              </span>
                            )}
                            {isMobile ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className="text-xl focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {codeToFlag(country.code)}
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent side="bottom" align="start" className="w-auto max-w-[180px] p-3">
                                  <div className="flex flex-col gap-1">
                                    <span className="text-sm font-semibold text-foreground">
                                      {countryLabel(country.code)}
                                    </span>
                                    <span className="text-muted-foreground text-xs tabular-nums">
                                      {formatInt(country.quantity)} units
                                    </span>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <>
                                <span className="text-muted-foreground text-xs font-light tabular-nums">
                                  {formatInt(country.quantity)} ×
                                </span>
                                <span className="text-xl">{codeToFlag(country.code)}</span>
                                <span className="font-semibold truncate">{countryLabel(country.code)}</span>
                              </>
                            )}
                          </div>
                          {skus.length === 0 && (
                            <Badge
                              variant="secondary"
                              className="absolute top-1 right-1 h-6 px-2 gap-0.5 cursor-default shrink-0 bg-muted/50 hover:bg-muted/80"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span className="text-[11px] font-normal px-0.5 whitespace-nowrap text-muted-foreground">
                                No matches
                              </span>
                            </Badge>
                          )}
                        </TableCell>
                        {columnOrder.map(columnId => {
                          if (columnId === 'country' || !isVisible(columnId)) return null
                          const isLast = columnId === lastVisibleColumnId
                          return (
                            <React.Fragment key={columnId}>
                              {renderCellContent(columnId, country, 'country', skus.length === 0, isLast)}
                            </React.Fragment>
                          )
                        })}
                                              </TableRow>

                      {/* SKU Rows (when expanded) */}
                      {isExpanded && skus.length > 0 && skus.map((sku, index) => {
                        const displayName = productLabel(sku.sku)
                        const imageUrl = getGroup(sku.sku)?.imageUrl ?? null
                        const skuCancellation = cancellationsMap.get(sku.sku)

                        return (
                          <TableRow
                            key={`${country.id}-${sku.sku}-${index}`}
                            id={index === 0 ? `skus-${country.id}` : undefined}
                            className="group border-t border-white/5 transition-colors bg-[oklch(0.18_0.016_290)] hover:bg-white/5"
                          >
                            <TableCell className={cn(
                              "text-left",
                              isMobile ? "p-2 w-14 min-w-14 max-w-14" : "p-4 w-1/4 min-w-44 max-w-1/4"
                            )}>
                              <div className={cn("flex items-start gap-3 min-w-0 max-w-sm", !isMobile && "ml-6")}>
                                {isMobile ? (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button type="button" className="shrink-0 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-md">
                                        <Thumbnail alt={displayName} src={imageUrl} size={36} className="rounded-md ring-1 ring-border/50" />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent side="bottom" align="start" className="w-auto max-w-[200px] p-3">
                                      <div className="flex flex-col gap-1">
                                        <span className="text-sm font-medium leading-tight text-foreground">
                                          {displayName}
                                        </span>
                                        <span className="text-muted-foreground text-xs tabular-nums">
                                          {formatInt(sku.quantity)} units
                                        </span>
                                        {skuCancellation && skuCancellation.quantity > 0 && (
                                          <div className="mt-1">
                                            <ReturnsIndicator
                                              quantity={skuCancellation.quantity}
                                              potentialRevenueLoss={skuCancellation.potentialRevenueLoss}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                ) : (
                                  <>
                                    <Thumbnail alt={displayName} src={imageUrl} size={40} className="shrink-0 rounded-md ring-1 ring-border/50" />
                                    <div className="flex flex-col gap-0.5 min-w-0 justify-center min-h-10">
                                      <div className="flex items-center gap-1.5 min-w-0">
                                        <span className="text-sm leading-tight line-clamp-2 whitespace-normal text-white/90" title={displayName}>
                                          {displayName}
                                        </span>
                                        {skuCancellation && skuCancellation.quantity > 0 && (
                                          <ReturnsIndicator
                                            quantity={skuCancellation.quantity}
                                            potentialRevenueLoss={skuCancellation.potentialRevenueLoss}
                                          />
                                        )}
                                      </div>
                                      <span className="text-muted-foreground text-xs font-light tabular-nums">
                                        {formatInt(sku.quantity)} ×
                                      </span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </TableCell>
                            {columnOrder.map(columnId => {
                              if (columnId === 'country' || !isVisible(columnId)) return null
                              const isLast = columnId === lastVisibleColumnId
                              return (
                                <React.Fragment key={columnId}>
                                  {renderCellContent(columnId, sku, 'sku', false, isLast)}
                                </React.Fragment>
                              )
                            })}
                                                      </TableRow>
                        )
                      })}
                    </React.Fragment>
                  )
                })
              )
            )}
          </TableBody>
        </table>
          </div>
          {!isBelowDesktop && (
            <div
              data-shelf-keep-open
              inert={!shelfOpen}
              className={cn(
                'shrink-0 overflow-x-clip transition-[width] duration-300',
                shelfOpen ? 'w-[256px]' : 'w-0'
              )}
            >
              <ColumnShelf
                sections={shelfSections}
                isDropTarget={isShelfDropTarget}
                isDragActive={activeHeaderColumn !== null}
                dropHint={t('tables.performanceBySku.shelf.dropToHide', {
                  defaultValue: 'Drop here to hide',
                })}
                onAddColumn={appendColumn}
                disabled={!shelfOpen}
                title={t('tables.performanceBySku.shelf.title', { defaultValue: 'More columns' })}
                emptyHint={t('tables.performanceBySku.shelf.empty', {
                  defaultValue: 'All columns are shown',
                })}
                className="sticky top-16 m-2 w-[240px]"
              />
            </div>
          )}
        </div>
        <DragOverlay dropAnimation={null}>
          {activeChipColumn ? (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-ring bg-popover px-2.5 py-2 text-sm shadow-lg">
              <span className="truncate text-foreground/90">
                {columnsConfig.find(c => c.id === activeChipColumn)?.label.replace(/\n/g, ' ')}
              </span>
              <span className="shrink-0 font-mono text-xs text-muted-foreground">
                {chipValue(activeChipColumn)}
              </span>
            </div>
          ) : activeHeaderColumn ? (
            // Header ghost: sized by DragOverlay to the source cell's rect.
            // Rendering it also flips dnd-kit's source-cell transform from
            // pointer-follow to slot displacement (see DraggableTableHead).
            <div className="flex h-full w-full cursor-grabbing items-center justify-center rounded-md border border-ring bg-popover/95 px-2 text-xs font-medium text-foreground shadow-lg">
              <span className="truncate">
                {columnsConfig.find(c => c.id === activeHeaderColumn)?.label.replace(/\n/g, ' ')}
              </span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  )
}
